import { Component, ElementRef, Inject, AfterViewInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { isObservable, Observable, Subscription } from 'rxjs';

import { ContainerAttachmentsService } from '../../containers/attachments/container-attachments.service';
import { firstValueFrom } from '../../shared/observable.utils';

export interface HTMLViewerDialogData {
  attachmentUrl?: string
  html?: string
  html$?: Observable<string>
  iframeSrc?: string
  title?: string
}

@Component({
  host: {
    class: 'mat-dialog-component',
    'test-id': 'html-viewer-dialog',
  },
  selector: 'html-viewer-dialog',
  styleUrls: ['html-viewer-dialog.component.scss'],
  templateUrl: './html-viewer-dialog.component.html',
})
export class HTMLViewerDialog implements AfterViewInit {
  public html: string
  public iframeSrc: SafeResourceUrl
  public isLoading = true
  public title: string

  private subscriptions = {
    html: Subscription.EMPTY,
  }

  @ViewChild('iframe', { read: ElementRef }) iframe: ElementRef

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: HTMLViewerDialogData,
    private containerAttachments: ContainerAttachmentsService,
    sanitizer: DomSanitizer,
  ) {
    this.title = data.title;
    if (data.iframeSrc) {
      this.iframeSrc = sanitizer.bypassSecurityTrustResourceUrl(data.iframeSrc);
    } else if (isObservable(data.html$)) {
      this.subscriptions.html = this.data.html$.subscribe((html) => this.writeHTML(html));
    } else {
      this.html = data.html;
    }
  }

  ngAfterViewInit(): void {
    if (!this.iframeSrc) {
      this.writeHTML(this.html);
    }
  }

  ngOnDestroy(): void {
    for (const subscription of Object.values(this.subscriptions)) {
      subscription.unsubscribe();
    }
  }

  onLoadIframe(): void {
    setTimeout(() => this.isLoading = false);
  }

  async writeHTML(html: string): Promise<void> {
    if (this.data.attachmentUrl) {
      // proxy XMLHttpRequestion.open() to ensure GET requests have the appropriate path
      const fileIdentifier = this.containerAttachments.getFileIdentifierFromUrl(this.data.attachmentUrl);
      const memberUrlTemplate = await firstValueFrom(this.containerAttachments.zipMemberUrl(fileIdentifier, '__MEMBER__'));
      const getMemberUrl = (memberPath: string): string => memberUrlTemplate.replace('__MEMBER__', encodeURIComponent(memberPath));
      const proxied = this.iframe.nativeElement.contentWindow.XMLHttpRequest.prototype.open;
      this.iframe.nativeElement.contentWindow.XMLHttpRequest.prototype.open = function() {
        /* eslint-disable prefer-rest-params */
        // intercept GET requests for relative resources
        if (arguments[0].toUpperCase() === 'GET' && !arguments[1].startsWith('http')) {
          const path = arguments[1].replace(/^[./]*/, '');
          arguments[1] = getMemberUrl(path);
        }
        return proxied.apply(this, [].slice.call(arguments));
      };
    }
    // write html to document
    this.iframe.nativeElement.contentDocument.write(html);
    this.iframe.nativeElement.contentDocument.close();
  }
}
